import { Button } from '@devizy/ui';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';

import { BidderCandidatureClient } from '@/components/bid/bidder-candidature-client';
import { BidsCount } from '@/components/bid/bids-count';
import { PageHeader } from '@/components/dashboard/page-header';
import { trpc } from '@/lib/trpc/client';
import { trpcVanilla } from '@/lib/trpc/shared';

export const Route = createFileRoute('/_app/offers/$id/bid')({
  beforeLoad: async () => {
    const me = await trpcVanilla.user.me.query().catch(() => null);
    if (!me) {
      throw redirect({ to: '/sign-in' });
    }
    if (me.role !== 'BIDDER') {
      throw redirect({ to: '/dashboard' });
    }
    return {
      currency: me.currency,
      verified:
        me.bidderVerificationStatus === 'VERIFIED' ||
        me.bidderVerificationStatus === 'PENDING_REVIEW',
      bidderType: me.bidderType,
    };
  },
  component: BidPage,
});

function BidPage(): React.ReactElement {
  const { id } = Route.useParams();
  const { currency, verified, bidderType } = Route.useRouteContext();
  const t = useTranslations('bid.candidature');
  const offerQuery = trpc.offer.byIdForBidder.useQuery({ offerId: id }, { enabled: verified });

  if (!verified) {
    return (
      <div className="flex flex-col gap-6" data-testid="bid-verification-required">
        <PageHeader title={t('verifyTitle')} subtitle={t('verifySubtitle')} />
        <div className="border-border bg-card rounded-2xl border p-8 text-center">
          <p className="text-muted-foreground mx-auto max-w-md text-sm">{t('verifyBody')}</p>
          <Button asChild className="mt-5">
            <Link
              to={
                bidderType === 'COMPANY' ? '/profile/bidder/company' : '/profile/bidder/individual'
              }
            >
              {t('verifyCta')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (offerQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Chargement…</p>;
  }

  if (offerQuery.isError || !offerQuery.data) {
    return (
      <div
        className="border-border bg-card rounded-2xl border p-8 text-center"
        data-testid="bid-offer-not-found"
      >
        <h1 className="font-display text-base">Offre introuvable</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Cette offre n&apos;existe pas ou ne vous est pas accessible.
        </p>
        <Link
          to="/dashboard/bidder/feed"
          className="text-primary mt-4 inline-block text-sm font-medium hover:underline"
        >
          Retour au feed
        </Link>
      </div>
    );
  }

  const title = offerQuery.data.title ?? '';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${t('title')} « ${title} »`}
        subtitle={t('subtitle')}
        actions={<BidsCount count={offerQuery.data.bidsCount} />}
      />
      <BidderCandidatureClient offerId={id} requesterCurrency={currency} />
    </div>
  );
}
